import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2, Smartphone, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * App-store links + web promo toggle for the Ronin app.
 * - google_play_url / app_store_url: shown as download badges on the public /ronin-box promo page.
 * - promo_enabled: when ON, /ronin-box is the app advertisement; when OFF, /ronin-box shows the
 *   full browser workout app instead. The full app is always available at /ronin-box/app.
 */
export default function RoninAppPromo() {
  const [form, setForm] = useState({ google_play_url: "", app_store_url: "", promo_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/ronin-box/app-promo")
      .then(({ data }) => setForm(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/ronin-box/app-promo", {
        google_play_url: form.google_play_url || "",
        app_store_url: form.app_store_url || "",
        promo_enabled: form.promo_enabled,
      });
      setForm(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/ronin-box" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-ronin-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
            <h1 className="font-display text-2xl uppercase tracking-tight">App Store Links</h1>
          </div>
        </div>
        <a href="/ronin-box" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest font-bold text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid="view-promo-page">
          <ExternalLink className="h-4 w-4" /> View promo page
        </a>
      </div>

      <div className="p-6 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-zinc-400">
              Paste your store listing URLs here. They appear as download badges on the public <span className="text-zinc-200 font-bold">/ronin-box</span> promo page. Leave a field blank to show a &quot;Coming soon&quot; badge for that store.
            </p>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2 mb-2">
                <Smartphone className="h-3.5 w-3.5" /> Google Play URL
              </label>
              <Input
                value={form.google_play_url}
                onChange={(e) => setForm({ ...form, google_play_url: e.target.value })}
                placeholder="https://play.google.com/store/apps/details?id=…"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs"
                data-testid="promo-google-play-url"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2 mb-2">
                <Smartphone className="h-3.5 w-3.5" /> Apple App Store URL
              </label>
              <Input
                value={form.app_store_url}
                onChange={(e) => setForm({ ...form, app_store_url: e.target.value })}
                placeholder="https://apps.apple.com/app/…"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs"
                data-testid="promo-app-store-url"
              />
            </div>

            <div className="flex items-start justify-between gap-4 border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4">
              <div>
                <div className="text-sm font-bold text-white uppercase tracking-widest">Promo mode</div>
                <p className="text-xs text-zinc-500 mt-1 max-w-md">
                  When ON, <span className="text-zinc-300">/ronin-box</span> is the app advertisement. When OFF, visitors get the full browser workout app instead. Either way, the web app is always available at <span className="text-zinc-300">/ronin-box/app</span>.
                </p>
              </div>
              <Switch
                checked={form.promo_enabled}
                onCheckedChange={(v) => setForm({ ...form, promo_enabled: v })}
                data-testid="promo-enabled-toggle"
              />
            </div>

            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="promo-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
